import * as CookieConsent from "vanilla-cookieconsent";
import "vanilla-cookieconsent/dist/cookieconsent.css";
import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { isEuCountry } from "../lib/eu-countries";
import { getGeoCountry } from "../lib/geo";

type ConsentState = {
	analytics: boolean;
	necessary: boolean;
	geo: { country: string; isEu: boolean };
	showPreferences: () => void;
};

const ConsentContext = createContext<ConsentState | null>(null);

export function ConsentProvider({ children }: { children: ReactNode }) {
	const country = useMemo(() => getGeoCountry(), []);
	const isEu = useMemo(() => isEuCountry(country), [country]);
	const [analytics, setAnalytics] = useState(!isEu);

	useEffect(() => {
		if (!isEu) return;

		CookieConsent.run({
			categories: {
				necessary: {
					enabled: true,
					readOnly: true,
				},
				analytics: {
					enabled: false,
				},
			},
			cookie: {
				expiresAfterDays: 365,
			},
			guiOptions: {
				consentModal: {
					layout: "bar",
					position: "bottom",
					equalWeightButtons: true,
				},
			},
			onConsent: () => {
				setAnalytics(CookieConsent.acceptedCategory("analytics"));
			},
			onChange: () => {
				setAnalytics(CookieConsent.acceptedCategory("analytics"));
			},
			onModalReady: ({ modal }) => {
				modal.setAttribute("data-testid", "cookie-consent-banner");
			},
			language: {
				default: "en",
				translations: {
					en: {
						consentModal: {
							title: "We use cookies",
							description:
								"This site uses essential cookies to function and analytics cookies to improve your experience. You can accept or reject non-essential cookies.",
							acceptAllBtn: "Accept all",
							acceptNecessaryBtn: "Reject all",
							showPreferencesBtn: "Manage preferences",
							footer: '<a href="/privacy">Privacy Policy</a>',
						},
						preferencesModal: {
							title: "Cookie preferences",
							acceptAllBtn: "Accept all",
							acceptNecessaryBtn: "Reject all",
							savePreferencesBtn: "Save preferences",
							closeIconLabel: "Close",
							sections: [
								{
									title: "Cookie usage",
									description:
										"We use cookies to ensure the basic functionality of the website and to enhance your experience.",
								},
								{
									title: "Essential cookies",
									description:
										"These cookies are necessary for the website to function and cannot be disabled.",
									linkedCategory: "necessary",
								},
								{
									title: "Analytics cookies",
									description:
										"These cookies help us understand how visitors interact with the website.",
									linkedCategory: "analytics",
								},
							],
						},
					},
				},
			},
		});
	}, [isEu]);

	const value = useMemo<ConsentState>(
		() => ({
			analytics,
			necessary: true,
			geo: { country, isEu },
			showPreferences: () => CookieConsent.showPreferences(),
		}),
		[analytics, country, isEu],
	);

	return (
		<ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
	);
}

export function useConsent(): ConsentState {
	const ctx = useContext(ConsentContext);
	if (!ctx) throw new Error("useConsent must be used within ConsentProvider");
	return ctx;
}
